/**
 * Custom Track analysis (FS-09 §2.4, a lite slice of FS-04's chart pipeline). Runs entirely
 * on-device — the file and any derived chart data are never uploaded (R26). Tempo is estimated by
 * autocorrelating an onset-emphasised energy envelope; low confidence falls back to a fixed nominal
 * tempo with a warning rather than failing silently, which is FS-04 F9's reduced-quality path
 * applied to tempo detection specifically rather than full beat-grid analysis.
 */
export const CUSTOM_MIN_DURATION_S = 45;
export const CUSTOM_MAX_DURATION_S = 12 * 60;
export const CUSTOM_MAX_FILE_BYTES = 60 * 1024 * 1024;
export const DEFAULT_NOMINAL_BPM = 120;

export interface CustomTrackResult {
  readonly buffer: AudioBuffer;
  readonly bpm: number;
  readonly digest: string;
  readonly warning: string | null;
}

export class CustomTrackError extends Error {}

/** Decode, validate and tempo-estimate a user-supplied track. Throws `CustomTrackError` (F12, F13). */
export async function analyzeCustomTrack(file: File, ctx: AudioContext): Promise<CustomTrackResult> {
  if (file.size > CUSTOM_MAX_FILE_BYTES) {
    throw new CustomTrackError(
      `"${file.name}" is ${(file.size / 1e6).toFixed(0)} MB — the limit is ${CUSTOM_MAX_FILE_BYTES / 1e6} MB.`,
    );
  }

  const raw = await file.arrayBuffer();
  const digest = await sha256Hex(raw);

  let buffer: AudioBuffer;
  try {
    buffer = await ctx.decodeAudioData(raw.slice(0));
  } catch {
    throw new CustomTrackError(`Could not decode "${file.name}" — unsupported codec.`);
  }

  if (buffer.duration < CUSTOM_MIN_DURATION_S) {
    throw new CustomTrackError(
      `Track is ${buffer.duration.toFixed(0)}s — Custom Track needs at least ${CUSTOM_MIN_DURATION_S}s.`,
    );
  }
  if (buffer.duration > CUSTOM_MAX_DURATION_S) {
    throw new CustomTrackError(
      `Track is ${(buffer.duration / 60).toFixed(1)} min — the limit is ${CUSTOM_MAX_DURATION_S / 60} min.`,
    );
  }

  const estimate = estimateBpm(buffer);
  const bpm = estimate ?? DEFAULT_NOMINAL_BPM;
  const warning =
    estimate === null
      ? `No clear beat detected — playing at a fixed ${DEFAULT_NOMINAL_BPM} BPM instead of the track's own tempo.`
      : null;

  return { buffer, bpm, digest, warning };
}

async function sha256Hex(data: ArrayBuffer): Promise<string> {
  const hash = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

const ENVELOPE_HZ = 100; // energy-envelope sample rate
const MIN_BPM = 70;
const MAX_BPM = 180;
const ANALYSIS_SECONDS = 60; // enough to find a stable tempo without walking the whole track
const CONFIDENCE_THRESHOLD = 1.4; // best autocorrelation peak vs. the search range's mean

/** Autocorrelate an onset envelope over the BPM search range. Returns null below confidence. */
function estimateBpm(buffer: AudioBuffer): number | null {
  const sr = buffer.sampleRate;
  const analysisLen = Math.min(buffer.length, Math.floor(ANALYSIS_SECONDS * sr));
  const channels = buffer.numberOfChannels;
  const chData: Float32Array[] = [];
  for (let c = 0; c < channels; c++) chData.push(buffer.getChannelData(c));

  const hop = Math.max(1, Math.floor(sr / ENVELOPE_HZ));
  const envLen = Math.floor(analysisLen / hop);
  const env = new Float32Array(envLen);
  for (let i = 0; i < envLen; i++) {
    const start = i * hop;
    const end = Math.min(analysisLen, start + hop);
    let sum = 0;
    for (let j = start; j < end; j++) {
      let s = chData[0]![j]!;
      for (let c = 1; c < channels; c++) s += chData[c]![j]!;
      sum += Math.abs(s / channels);
    }
    env[i] = sum / Math.max(1, end - start);
  }

  const onset = new Float32Array(envLen);
  for (let i = 1; i < envLen; i++) onset[i] = Math.max(0, env[i]! - env[i - 1]!);

  const minLag = Math.round((60 / MAX_BPM) * ENVELOPE_HZ);
  const maxLag = Math.round((60 / MIN_BPM) * ENVELOPE_HZ);
  let bestLag = -1;
  let bestScore = 0;
  let totalScore = 0;
  let lagCount = 0;
  for (let lag = minLag; lag <= maxLag; lag++) {
    let score = 0;
    for (let i = lag; i < envLen; i++) score += onset[i]! * onset[i - lag]!;
    totalScore += score;
    lagCount++;
    if (score > bestScore) {
      bestScore = score;
      bestLag = lag;
    }
  }
  if (bestLag <= 0 || totalScore <= 0) return null;

  const confidence = bestScore / (totalScore / lagCount);
  if (confidence <= CONFIDENCE_THRESHOLD) return null;

  // Fold into a sane octave — autocorrelation often finds a half- or double-tempo peak.
  let bpm = (60 * ENVELOPE_HZ) / bestLag;
  while (bpm < MIN_BPM) bpm *= 2;
  while (bpm > MAX_BPM) bpm /= 2;
  return Math.round(bpm);
}
